import { NavigationWrapper } from "../components/navigation/navigationWrapper.tsx";
import { Heading, Text, Alert, Button } from "@idg-web-platform/pulse";
import css from "./permissionsPage.module.css";
import margins from "@idg-web-platform/pulse/cssModules/margins.module.css";
import { useEffect, useState } from "preact/hooks";
import { analyticsContentViewed } from "../utils/analyticsContentViewed.ts";

const PermissionsPage = ({
  initialPermissions,
  saved,
}: {
  initialPermissions: Permissions;
  saved: boolean;
}) => {
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [isButtonVisible, setIsButtonVisible] = useState(false);
  const [alertDescription, setAlertDescription] = useState("");
  const [persistentPermissionsState, setPersistentPermissionsState] =
    useState<Permissions>(initialPermissions);
  const [permissionsState, setPermissionsState] = useState<Permissions>(initialPermissions);
  const [isSavedAlertVisible, setIsSavedAlertVisible] = useState(saved);

  // if user's club is not ready or not available, the user should not be able to see the permissions
  const postPermissionAvailable = permissionsState.clubPost !== null;
  const emailPermissionAvailable = permissionsState.clubEmail !== null;

  if (!postPermissionAvailable && !emailPermissionAvailable) {
    setIsAlertVisible(true);
    setAlertDescription("Deine Werbeeinstellungen sind derzeit nicht verfügbar.");
  }

  const handleChange = (property: "clubPost" | "clubEmail", checked: boolean) => {
    setIsSavedAlertVisible(false);
    setPermissionsState((prevState: Permissions) => {
      const updatedState = { ...prevState, [property]: checked };
      const changed =
        updatedState.clubPost !== persistentPermissionsState.clubPost ||
        updatedState.clubEmail !== persistentPermissionsState.clubEmail;

      // if one of the permissions was selected and now is deselected, show alert
      const deselected =
        (!updatedState.clubPost && !!persistentPermissionsState.clubPost) ||
        (!updatedState.clubEmail && !!persistentPermissionsState.clubEmail);

      if (!updatedState.clubEmail && !updatedState.clubPost) {
        setAlertDescription("Bist du sicher, dass du keine Angebote erhalten möchtest?");
      } else if (!updatedState.clubPost) {
        setAlertDescription("Bist du sicher, dass du keine Angebote per Post erhalten möchtest?");
      } else if (!updatedState.clubEmail) {
        setAlertDescription("Bist du sicher, dass du keine Angebote per E-Mail erhalten möchtest?");
      }

      setIsAlertVisible(deselected);
      setIsButtonVisible(changed);

      return updatedState;
    });
  };

  const handleSubmit = async (event: Event) => {
    event.preventDefault();

    const data = {
      clubPost: !!permissionsState.clubPost,
      clubEmail: !!permissionsState.clubEmail,
    };

    const response = await fetch("/api/c/club/permissions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (response.status !== 204) {
      console.log(response);
      throw new Error("Failed to save permissions");
    }
    setPersistentPermissionsState(permissionsState);
    setIsButtonVisible(false);
    setIsAlertVisible(false);
    setIsSavedAlertVisible(true);
  };

  useEffect(() => {
    analyticsContentViewed("permissions");
  }, []);

  return (
    <>
      <NavigationWrapper currentPath="/c/club/permissions">
        <Heading sizeStep={2} text="Werbeeinstellungen" element="h1" />
        {isSavedAlertVisible && (
          <Alert
            type="success"
            headline="Erfolgreich gespeichert"
            description="Deine Änderungen wurden erfolgreich gespeichert."
            class={margins.pulseMarginBottomS}
            onClose={() => setIsSavedAlertVisible(false)}
          />
        )}
        <Text class={margins.pulseMarginBottomL}>
          Behalte den Überblick, wir halten dich auf dem Laufenden.
        </Text>
        <Heading
          sizeStep={1}
          text="Deine Abonnements:"
          element="h2"
          class={css.subline}
          removeMargin={true}
        />
        <form onSubmit={handleSubmit}>
          <ul className={css.aboList}>
            {postPermissionAvailable && (
              <li className={css.aboListItem}>
                <input
                  type="checkbox"
                  id="clubPost"
                  name="clubPost"
                  checked={permissionsState.clubPost}
                  onInput={(event) => handleChange("clubPost", event.currentTarget.checked)}
                  data-testid="permissions-clubPost"
                />
                <label htmlFor="clubPost">CLUB - Angebote per Post</label>
              </li>
            )}
            {emailPermissionAvailable && (
              <li className={css.aboListItem}>
                <input
                  type="checkbox"
                  id="clubEmail"
                  name="clubEmail"
                  checked={permissionsState.clubEmail}
                  onInput={(event) => handleChange("clubEmail", event.currentTarget.checked)}
                  data-testid="permissions-clubEmail"
                />
                <label htmlFor="clubEmail">CLUB - Angebote per E-Mail</label>
              </li>
            )}
          </ul>
          {isAlertVisible && (
            <Alert
              type="warning"
              headline="CLUB-Angebote"
              description={alertDescription}
              onClose={() => setIsAlertVisible(false)}
              class={margins.pulseMarginBottomS}
              showCloseButton={false}
            />
          )}
          {isButtonVisible && (
            <Button
              text="Änderung speichern"
              level="primary"
              type="submit"
              data-testid="permissions-submit"
            />
          )}
        </form>
      </NavigationWrapper>
    </>
  );
};

PermissionsPage.tagName = "club-permissions-page";

interface Permissions {
  clubPost?: boolean;
  clubEmail?: boolean;
}

export { PermissionsPage };
export type { Permissions };
